/**
 * contact_methods — several labeled emails and phones per record, one primary
 * per kind (issue #402).
 *
 * A record (a Contact, a claim insured, a contractor, a carrier, a lead, an
 * external appraiser or a user) can hold many labeled emails and phones instead
 * of the single fixed columns it has today. Each row is one email or one phone
 * for one owner; `is_primary` marks the one the legacy single-value columns and
 * the default pickers use.
 *
 * Firm-scoped like every other record table: `org_id` is NOT NULL and the table
 * is under per-firm Row Level Security. It is created by the hand-run manual
 * migration drizzle/manual/0106_contact-methods.sql (not journaled — the drizzle
 * journal is out of step with drizzle/, MIS-86 — so part-1 ships the table by
 * hand like 0105). The SQL there must stay in step with this definition.
 *
 * PART 1 scope: the table, its RLS and the repository only. No legacy column is
 * read, written or backfilled yet — that is parts 2+.
 *
 * `owner_id` is text, not uuid, because the owner kinds do not share one id
 * type: users.id is a text (Firebase) uid while contacts/claims/etc. are uuids.
 * Dedupe is by `value_normalized` (lowercased email, or phone digits+ext from
 * #401's normalizePhone), so `Foo@Example.com`/`foo@example.com` and
 * `(555) 123-4567`/`555-123-4567` collapse to one row.
 */
import { sql } from 'drizzle-orm';
import {
  pgTable,
  uuid,
  text,
  boolean,
  timestamp,
  index,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import { organizations } from './organizations';

/** The record a contact method hangs off. Mirrors the 0106 owner_type CHECK. */
export const CONTACT_METHOD_OWNER_TYPES = [
  'contact',
  'claim_insured_1',
  'claim_insured_2',
  'contractor',
  'carrier',
  'lead',
  'external_appraiser',
  'user',
] as const;
export type ContactMethodOwnerType = (typeof CONTACT_METHOD_OWNER_TYPES)[number];

/** Email or phone. Mirrors the 0106 kind CHECK. */
export const CONTACT_METHOD_KINDS = ['email', 'phone'] as const;
export type ContactMethodKind = (typeof CONTACT_METHOD_KINDS)[number];

export const contactMethods = pgTable(
  'contact_methods',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    orgId: uuid('org_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    ownerType: text('owner_type').notNull(),
    ownerId: text('owner_id').notNull(),
    kind: text('kind').notNull(),
    /** work | mobile | home | office | other, or free text. */
    label: text('label'),
    /** The value exactly as entered (for display of international numbers etc.). */
    valueRaw: text('value_raw').notNull(),
    /** Lowercased email, or phone digits+ext (#401 normalizePhone). Dedupe key. */
    valueNormalized: text('value_normalized').notNull(),
    isPrimary: boolean('is_primary').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    // Dedupe: one row per normalized value for an owner+kind within a firm.
    uniqueIndex('contact_methods_dedupe_uq').on(
      table.orgId,
      table.ownerType,
      table.ownerId,
      table.kind,
      table.valueNormalized,
    ),
    // At most one primary per firm+owner+kind (partial unique). org_id leads the key
    // because a `user` owner (a Firebase uid) can belong to more than one firm via
    // user_organizations; without org_id, firm A's primary would block firm B from
    // ever setting its own primary for that same uid. Every repo query is firm-scoped,
    // so "one primary per owner+kind" is always evaluated within a single firm.
    uniqueIndex('contact_methods_one_primary_uq')
      .on(table.orgId, table.ownerType, table.ownerId, table.kind)
      .where(sql`is_primary`),
    index('idx_contact_methods_org_id').on(table.orgId),
    index('idx_contact_methods_owner').on(table.ownerType, table.ownerId),
  ],
);

export type ContactMethod = typeof contactMethods.$inferSelect;
export type InsertContactMethod = typeof contactMethods.$inferInsert;
